import React, { useRef, useState } from 'react';
import { importStatement } from '../../services/api';
import {
  acceptImport,
  addImport,
  compactRecord,
  dismissImport,
  findDuplicate,
  importGroups,
  importSummary,
  importTooLarge,
  MAX_IMPORT_FILES,
  removeImport,
  replacedFiles,
} from './importMerge';
import { importCopy } from './importCopy';
import { IMPORT_TARGET_SECTIONS, IMPORT_TARGETS, INCOME_SOURCE } from './importTargets';

const UNSURE_SHOWN = 10;

const DOCUMENT_LABELS = {
  payroll_or_tax: 'Pay or tax document', w2: 'W-2', pay_stub: 'Pay stub', tax_return: 'Tax return',
  investment: 'Savings or investment statement', retirement_statement: 'Retirement statement',
  '529_statement': '529 statement', brokerage_statement: 'Brokerage statement',
  loan: 'Loan statement', mortgage_statement: 'Mortgage statement', home_equity_statement: 'Home equity statement',
  auto_loan_statement: 'Auto loan statement',
  student_loan_statement: 'Student loan statement', insurance: 'Insurance statement',
  insurance_statement: 'Insurance statement', benefits: 'Benefits summary', benefits_summary: 'Benefits summary',
  social_security_statement: 'Social Security statement',
};

const fmt = (n) =>
  '$' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function periodText(file, replaced) {
  if (replaced.has(file.id)) return 'Replaced by a newer statement';
  if (file.doc_kind === 'document') return DOCUMENT_LABELS[file.doc_type] || 'Document';
  if (file.is_annual || file.layout === 'summary') return 'Year-end summary';
  if (file.period_start && file.period_end) {
    return `${fmtDate(file.period_start)} – ${fmtDate(file.period_end)}`;
  }
  return 'Transactions';
}

function coverageText(group) {
  if (group.annual) return null;
  const months = Math.max(1, Math.round(group.days / 30.44));
  const span = months === 1 ? 'about a month' : `about ${months} months`;
  const count = group.files.length;
  const statements = `${count} ${count === 1 ? 'statement' : 'statements'} covering ${span}`;
  return group.factor === 1 ? statements : `${statements}, scaled to a full year`;
}

function errorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  // 503/504: the server took longer than the API allows (a very large or unusual PDF).
  if ([503, 504].includes(err?.response?.status)) {
    return 'This file took too long to read. Please try again, or upload just the pages of the statement itself.';
  }
  if (err?.code === 'ECONNABORTED') return 'The upload timed out. Please try again.';
  return err?.message || 'Something went wrong reading this file.';
}

function newId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Field picker grouped by wizard section, for + on an entry with no suggestion. */
function FieldPicker({ label, onPick, onCancel }) {
  return (
    <span className="d-inline-flex align-items-center gap-1">
      <select
        className="form-select form-select-sm"
        defaultValue=""
        aria-label={label}
        style={{ maxWidth: 260 }}
        autoFocus
        onChange={(e) => e.target.value && onPick(e.target.value)}
      >
        <option value="">Where does this go?</option>
        {IMPORT_TARGET_SECTIONS.filter((s) => s.section !== 'income').map((section) => (
          <optgroup key={section.section} label={section.label}>
            {section.fields.map((f) => (
              <option key={f.field} value={`${section.section}.${f.field}`}>{f.label}</option>
            ))}
          </optgroup>
        ))}
      </select>
      <button type="button" className="btn btn-sm btn-link text-muted px-1" onClick={onCancel} aria-label="Cancel">
        <i className="bi bi-x-lg" />
      </button>
    </span>
  );
}

/**
 * Optional step right after Welcome: upload statements and documents to
 * pre-fill amounts. Each file is read by POST /wizard/import and checked by
 * the classifier (nothing is stored server-side); results are merged into
 * formData via importMerge — statements of one account are combined over
 * their joint period — and saved straight away through `onImportChange`.
 *
 * The page then says what was added (x removes it) and what the import
 * wasn't sure about (+ adds it, to the suggested field or one picked from a
 * list); the client completes everything else on the following pages.
 *
 * @param {object}   formData       - Full wizard form data.
 * @param {function} onImportChange - Called with updated formData after each
 *                                    change; the parent sets and persists it.
 * @param {Array}    importTypes    - [{key, label, featured}] the server accepts;
 *                                    the copy names the featured ones (see
 *                                    importCopy). Undefined: the general copy.
 */
export default function StepImport({ formData, onImportChange, importTypes }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploads, setUploads] = useState([]); // { name, status: 'uploading' | 'error', message }
  const [showAllUnsure, setShowAllUnsure] = useState(false);
  const [picking, setPicking] = useState(null); // entry key whose field picker is open

  const files = formData.import?.files || [];
  const groups = importGroups(files);
  const replaced = replacedFiles(files);
  const summary = importSummary(formData);
  const { intro, statements, spreadsheets, accept } = importCopy(importTypes);
  const busy = uploads.some((u) => u.status === 'uploading');

  function setUpload(name, upload) {
    setUploads((prev) => [...prev.filter((u) => u.name !== name), ...(upload ? [{ name, ...upload }] : [])]);
  }

  async function handleFiles(fileList) {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;

    let working = formData;
    for (const file of picked) {
      if ((working.import?.files || []).length >= MAX_IMPORT_FILES) {
        setUpload(file.name, {
          status: 'error',
          message: `You can import up to ${MAX_IMPORT_FILES} files. Remove some to add more.`,
        });
        continue;
      }
      setUpload(file.name, { status: 'uploading' });
      try {
        const { result, rejected } = await importStatement(file);
        if (rejected) {
          setUpload(file.name, { status: 'error', message: rejected });
          continue;
        }
        const duplicate = findDuplicate(working.import?.files, result);
        if (duplicate) {
          setUpload(file.name, {
            status: 'error',
            message: `Already imported as ${duplicate.filename} — skipped so it isn't counted twice.`,
          });
          continue;
        }
        const record = compactRecord(result, { id: newId(), importedAt: new Date().toISOString() });
        const next = addImport(working, record);
        if (importTooLarge(next.formData)) {
          setUpload(file.name, {
            status: 'error',
            message: "That's as many statements as we can keep. Remove a few to add more.",
          });
          continue;
        }
        working = next.formData;
        onImportChange(working);
        setUpload(file.name, null);
      } catch (err) {
        setUpload(file.name, { status: 'error', message: errorMessage(err) });
      }
    }
  }

  function handleRemove(fileId) {
    onImportChange(removeImport(formData, fileId).formData);
  }

  function handleDismiss(dismissKey) {
    onImportChange(dismissImport(formData, dismissKey).formData);
  }

  function handleAccept(entryKey, target) {
    setPicking(null);
    onImportChange(acceptImport(formData, entryKey, target).formData);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (!busy) handleFiles(e.dataTransfer.files);
  }

  const unsureShown = showAllUnsure ? summary.unsure : summary.unsure.slice(0, UNSURE_SHOWN);
  const hasSummary = summary.added.length > 0 || summary.kept.length > 0 || summary.unsure.length > 0;

  return (
    <div>
      <div className="mb-4">
        <h2 className="h4 fw-bold mb-2">Import statements <span className="text-muted fw-normal h6">(optional)</span></h2>
        <p className="text-muted mb-0">
          {intro} You'll review and adjust every number on the pages that follow.
        </p>
      </div>

      <div className="alert alert-light border-start border-4 border-danger py-3 mb-4">
        <h6 className="fw-bold mb-2">
          <i className="bi bi-lightbulb me-2 text-danger" />
          Good to know
        </h6>
        <ul className="mb-0 small text-muted ps-3">
          <li>Use the <strong>PDFs</strong> from each provider's website — usually under <em>Statements</em> or <em>Documents</em>.</li>
          {statements ? (
            <>
              <li>A <strong>year of monthly statements</strong> gives the best picture. A few recent months or a year-end summary works too; we'll scale it to a year.</li>
              <li>Add each account you spend from — checking and credit cards. You can select several files at once.</li>
            </>
          ) : (
            <li>Your <strong>most recent statement</strong> is enough; if you add a newer one later, it replaces the older one.</li>
          )}
          <li>{spreadsheets ? 'Spreadsheet downloads (CSV or Excel) work too. ' : ''}Scanned or photographed pages can't be read.</li>
          <li>Up to 4 MB per file. We read the numbers and don't keep the file.</li>
        </ul>
      </div>

      <div
        className={`cashflow-dropzone mb-3${dragOver ? ' cashflow-dropzone--active' : ''}${busy ? ' opacity-75' : ''}`}
        role="button"
        tabIndex={0}
        aria-label="Upload statements"
        onClick={() => !busy && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !busy) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <i className="bi bi-cloud-arrow-up fs-2 text-danger d-block mb-2" />
        <div className="fw-semibold">
          {busy ? 'Reading your files…' : 'Drag files here or click to choose'}
        </div>
        <div className="small text-muted">{spreadsheets ? 'PDFs, or CSV / Excel downloads' : 'PDFs'}</div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {uploads.map((u) => (
        <div
          key={u.name}
          className={`alert ${u.status === 'error' ? 'alert-warning' : 'alert-light border'} py-2 small d-flex align-items-start gap-2`}
        >
          {u.status === 'uploading' ? (
            <span className="spinner-border spinner-border-sm text-danger mt-1" role="status" />
          ) : (
            <i className="bi bi-exclamation-triangle mt-1" />
          )}
          <div className="flex-fill">
            <strong>{u.name}</strong>
            {u.status === 'uploading' ? ' — reading…' : <div>{u.message}</div>}
          </div>
          {u.status === 'error' && (
            <button
              type="button"
              className="btn-close btn-sm"
              aria-label="Dismiss"
              onClick={() => setUpload(u.name, null)}
            />
          )}
        </div>
      ))}

      {groups.map((group) => {
        const coverage = coverageText(group);
        return (
          <div key={group.key} className="cashflow-section-card">
            <div className="d-flex align-items-baseline gap-2 mb-1">
              <i className={`bi ${group.files[0]?.doc_kind === 'document' ? 'bi-file-earmark-text' : 'bi-bank'} text-danger`} />
              <span className="fw-semibold text-truncate">{group.label}</span>
            </div>
            {coverage && <div className="small text-muted mb-2">{coverage}</div>}

            <ul className="list-unstyled small mb-0">
              {group.files.map((file) => (
                <li key={file.id} className="d-flex align-items-center gap-2 py-1">
                  <i className={`bi ${file.source_type === 'pdf' ? 'bi-file-earmark-pdf' : 'bi-file-earmark-spreadsheet'} text-muted`} />
                  <span className="flex-fill text-truncate" style={{ minWidth: 0 }}>
                    {file.filename}
                    <span className="text-muted"> · {periodText(file, replaced)}</span>
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary text-nowrap py-0"
                    onClick={() => handleRemove(file.id)}
                    disabled={busy}
                    aria-label={`Remove ${file.filename}`}
                  >
                    <i className="bi bi-trash" />
                    <span className="d-none d-sm-inline ms-1">Remove</span>
                  </button>
                </li>
              ))}
            </ul>

            {[...new Set(group.files.flatMap((f) => f.warnings || []))].map((w) => (
              <div key={w} className="small text-muted mt-2">
                <i className="bi bi-info-circle me-1" />
                {w}
              </div>
            ))}
          </div>
        );
      })}

      {files.length > 0 && hasSummary && (
        <div className="cashflow-section-card">
          {summary.notices.map((notice) => (
            <div key={notice} className="alert alert-warning py-2 small mb-3">
              <i className="bi bi-exclamation-circle me-2" />
              {notice}
            </div>
          ))}

          <div className="fw-semibold mb-2">
            <i className="bi bi-check2-circle text-danger me-2" />
            What we added
          </div>
          {summary.added.length === 0 && (
            <div className="small text-muted">Nothing yet — add anything below that looks right with +.</div>
          )}
          {summary.added.length > 0 && (
            <ul className="list-unstyled small mb-0">
              {summary.added.map((a) => (
                <li key={a.dismissKey} className="d-flex align-items-center justify-content-between gap-2 py-1 border-bottom">
                  <span style={{ minWidth: 0 }}>
                    {a.label}
                    <span className="text-muted"> · {a.sectionLabel}</span>
                    {a.note && <span className="d-block text-muted">{a.note}</span>}
                  </span>
                  <span className="d-flex align-items-center gap-2">
                    {a.amount != null && <span className="fw-semibold text-nowrap">{fmt(a.amount)}/yr</span>}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary py-0 px-2"
                      onClick={() => handleDismiss(a.dismissKey)}
                      aria-label={`Remove ${a.label}`}
                      title="Remove"
                    >
                      <i className="bi bi-x-lg" />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}

          {(summary.kept.length > 0 || summary.unsure.length > 0) && (
            <div className="fw-semibold mt-4 mb-2">
              <i className="bi bi-question-circle text-danger me-2" />
              What we're not sure about
            </div>
          )}
          {summary.kept.length > 0 && (
            <div className="small mb-2">
              <div className="text-muted mb-1">You'd already entered these, so we left them as they were:</div>
              <ul className="list-unstyled mb-0">
                {summary.kept.map((k) => (
                  <li key={k.key} className="py-1">
                    {k.label}: yours {fmt(k.yours)}/yr, your files suggest about {fmt(k.statements)}/yr.
                  </li>
                ))}
              </ul>
            </div>
          )}
          {summary.unsure.length > 0 && (
            <div className="small">
              <div className="text-muted mb-1">
                We didn't add these ({fmt(summary.unsureTotal)}/yr). Use + for any that are regular
                expenses, or add them on the page where they fit.
              </div>
              <ul className="list-unstyled mb-0">
                {unsureShown.map((m) => {
                  const suggestion = m.suggested && m.suggested !== INCOME_SOURCE ? IMPORT_TARGETS[m.suggested] : null;
                  return (
                    <li key={m.key} className="d-flex align-items-center justify-content-between gap-2 py-1 border-bottom flex-wrap">
                      <span style={{ minWidth: 0 }}>
                        {m.label}
                        {m.category && m.category.toLowerCase() !== m.label.toLowerCase() && (
                          <span className="text-muted"> · {m.category}</span>
                        )}
                        {m.monthly && <span className="text-muted"> · monthly</span>}
                        {suggestion && <span className="d-block text-muted">Maybe {suggestion.label}?</span>}
                      </span>
                      <span className="d-flex align-items-center gap-2">
                        <span className="text-nowrap">{fmt(m.annual)}/yr</span>
                        {picking === m.key ? (
                          <FieldPicker
                            label={`Choose where ${m.label} goes`}
                            onPick={(target) => handleAccept(m.key, target)}
                            onCancel={() => setPicking(null)}
                          />
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger py-0 px-2"
                            onClick={() => (suggestion || m.suggested === INCOME_SOURCE
                              ? handleAccept(m.key, m.suggested)
                              : setPicking(m.key))}
                            aria-label={`Add ${m.label}`}
                            title={suggestion ? `Add to ${suggestion.label}` : 'Add'}
                          >
                            <i className="bi bi-plus-lg" />
                          </button>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {summary.unsure.length > UNSURE_SHOWN && (
                <button
                  type="button"
                  className="btn btn-link btn-sm px-0 mt-1 text-danger text-decoration-none"
                  onClick={() => setShowAllUnsure((v) => !v)}
                >
                  {showAllUnsure ? 'Show fewer' : `Show ${summary.unsure.length - UNSURE_SHOWN} more`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <p className="text-muted small mt-4 mb-0">
        {files.length > 0
          ? <>Click <strong>Next</strong> to review the amounts and fill in the rest, section by section.</>
          : <>Nothing handy? Just click <strong>Next</strong> to enter amounts yourself.</>}
      </p>
    </div>
  );
}
